import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { RequireAuth, RequireOnboarding, PublicOnly } from "@/lib/guards";
import WelcomeGate from "@/components/WelcomeGate";

import Landing from "@/pages/Landing";
import AuthLanding from "@/pages/auth/AuthLanding";
import AuthOptions from "@/pages/auth/AuthOptions";
import Signup from "@/pages/auth/Signup";
import Login from "@/pages/auth/Login";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Legal from "@/pages/auth/Legal";
import AppleCallback from "@/pages/auth/AppleCallback";

import ProfileBasics from "@/pages/onboarding/ProfileBasics";
import ProfilePhoto from "@/pages/onboarding/ProfilePhoto";
import Interests from "@/pages/onboarding/Interests";
import SuggestedPeople from "@/pages/onboarding/SuggestedPeople";

import AppShell from "@/components/AppShell";
import Home from "@/pages/app/Home";
import Discover from "@/pages/app/Discover";
import Opinions from "@/pages/app/Opinions";
import Following from "@/pages/app/Following";
import Reels from "@/pages/app/Reels";
import Create from "@/pages/app/Create";
import Notifications from "@/pages/app/Notifications";
import Profile from "@/pages/app/Profile";
import UserProfile from "@/pages/app/UserProfile";
import Search from "@/pages/app/Search";
import Settings from "@/pages/app/Settings";
import EditProfile from "@/pages/app/EditProfile";
import Saved from "@/pages/app/Saved";
import PostDetail from "@/pages/app/PostDetail";
import UserListPage from "@/pages/app/UserListPage";
import BlockedUsers from "@/pages/app/BlockedUsers";
import MyReports from "@/pages/app/MyReports";
import FollowRequests from "@/pages/app/FollowRequests";

import LegalPage from "@/pages/LegalPage";
import ComingSoon from "@/pages/ComingSoon";
import Support from "@/pages/Support";
import Help from "@/pages/Help";
import HelpDeleteAuth from "@/pages/HelpDeleteAuth";
import HelpDeleteConfirm from "@/pages/HelpDeleteConfirm";
import HelpDeleteSuccess from "@/pages/HelpDeleteSuccess";
import HelpDeleteAccountData from "@/pages/HelpDeleteAccountData";
import PublicProfileGate from "@/components/PublicProfileGate";

import "@/App.css";

// -------------------------------------------------------------------
// Temporary gate: while COMING_SOON_MODE is true, every route the user
// can reach AFTER auth (login/signup click, onboarding, or the whole
// app shell) resolves to <ComingSoon/>. The real pages remain wired
// below and untouched — flip this flag to `false` to bring them back.
// -------------------------------------------------------------------
const COMING_SOON_MODE = false;

// Keep the original component imports referenced so the codebase stays
// tree-shakeable-friendly and the intent (they're paused, not removed)
// is explicit.
void [
  Signup, Login, VerifyEmail, ForgotPassword, ResetPassword, Legal,
  ProfileBasics, ProfilePhoto, Interests, SuggestedPeople,
  AppShell, Home, Discover, Opinions, Following, Reels, Create,
  Notifications, Profile, UserProfile, Search, Settings, EditProfile, Saved, PostDetail,
  UserListPage, BlockedUsers, MyReports, FollowRequests,
];

function App() {
  // While in Coming-Soon mode: only the landing page renders its real
  // content. Every other route (auth, onboarding, app shell, legal even)
  // short-circuits to <ComingSoon/>. The signup/login buttons on the
  // landing page still navigate to /signup and /login — those paths just
  // now resolve to the ComingSoon screen.
  const gated = (real) => (COMING_SOON_MODE ? <ComingSoon /> : real);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster theme="system" position="bottom-right" richColors />
          {/* Renders the WelcomeSplash overlay whenever AuthContext.welcome
              is set — mounted at the App root so it survives route-guard
              redirects that would otherwise unmount page-level splashes. */}
          <WelcomeGate />
          <Routes>
            {/* Public — Landing.
                While COMING_SOON_MODE is on we intentionally SKIP the
                PublicOnly guard on `/`. That guard redirects any authed
                user (including anyone with a stale token in localStorage)
                to /home, which — in coming-soon mode — resolves to the
                ComingSoon screen. That both (a) made the site open at
                Coming Soon on first load and (b) broke the "Back to
                home" button by redirecting back to Coming Soon in a
                loop. Landing must always be reachable while the gate is
                on; the guard is restored automatically when the flag is
                flipped off. */}
            {/* Marketing / pre-signup landing page — KEEP EXACTLY AS DEPLOYED
                (Terms / Privacy / Community Guidelines / Support footer links
                stay on this page 1:1 with production).  The iOS-parity auth
                landing lives at /welcome and is reached from this page. */}
            <Route
              path="/"
              element={COMING_SOON_MODE ? <Landing /> : <PublicOnly><Landing /></PublicOnly>}
            />
            {/* Legacy alias for anyone still hitting /marketing */}
            <Route path="/marketing" element={<Landing />} />
            <Route path="/signup" element={gated(<PublicOnly><Signup /></PublicOnly>)} />
            <Route path="/login" element={gated(<PublicOnly><Login /></PublicOnly>)} />
            {/* iOS-parity auth entry — landing hero + options sheet.
                `/welcome` alias kept so previously shared links still work. */}
            <Route path="/welcome" element={<PublicOnly><AuthLanding /></PublicOnly>} />
            <Route path="/auth/options" element={<PublicOnly><AuthOptions /></PublicOnly>} />
            <Route path="/verify" element={gated(<VerifyEmail />)} />
            <Route path="/forgot" element={gated(<ForgotPassword />)} />
            <Route path="/reset" element={gated(<ResetPassword />)} />
            {/* Apple Sign-In callback — runs INSIDE the popup opened by
                useAppleSignIn(); reads ?code=&state= from Apple's
                redirect and postMessages back to the opener.  MUST NOT
                be wrapped in PublicOnly / RequireAuth. */}
            <Route path="/auth/apple/callback" element={<AppleCallback />} />
            {/* iOS-parity legal modal (Terms / Privacy / Guidelines switcher). */}
            <Route path="/legal" element={<Legal />} />
            {/* Legal pages stay reachable so we don't break footer links */}
            <Route path="/terms" element={<LegalPage variant="terms" />} />
            <Route path="/privacy" element={<LegalPage variant="privacy" />} />
            <Route path="/guidelines" element={<LegalPage variant="guidelines" />} />
            {/* Support / Contact — always reachable, even in coming-soon mode.
                Submits to POST /api/contact which routes to Postmark. */}
            <Route path="/support" element={<Support />} />
            <Route path="/contact" element={<Support />} />

            {/* Help hub — public.  Two paths: Get in Touch (→/support) and
                Delete Account (→/help/delete → auth → /confirm → /done). */}
            <Route path="/help" element={<Help />} />
            <Route path="/help/delete" element={<HelpDeleteAuth />} />
            <Route path="/help/delete-account" element={<HelpDeleteConfirm />} />
            <Route path="/help/delete/done" element={<HelpDeleteSuccess />} />
            <Route path="/help/delete-account-data" element={<HelpDeleteAccountData />} />

            {/* Onboarding */}
            <Route path="/onboarding/basics" element={gated(<RequireOnboarding><ProfileBasics /></RequireOnboarding>)} />
            <Route path="/onboarding/photo" element={gated(<RequireOnboarding><ProfilePhoto /></RequireOnboarding>)} />
            <Route path="/onboarding/interests" element={gated(<RequireOnboarding><Interests /></RequireOnboarding>)} />
            <Route path="/onboarding/people" element={gated(<RequireOnboarding><SuggestedPeople /></RequireOnboarding>)} />

            {/* App — every child of AppShell also gates to ComingSoon while
                the flag is on. When lifted, these routes return unchanged. */}
            {COMING_SOON_MODE ? (
              <>
                <Route path="/home" element={<ComingSoon />} />
                <Route path="/discover" element={<ComingSoon />} />
                <Route path="/opinions" element={<ComingSoon />} />
                <Route path="/following" element={<ComingSoon />} />
                <Route path="/reels" element={<ComingSoon />} />
                <Route path="/create" element={<ComingSoon />} />
                <Route path="/notifications" element={<ComingSoon />} />
                <Route path="/search" element={<ComingSoon />} />
                <Route path="/settings" element={<ComingSoon />} />
                <Route path="/settings/edit-profile" element={<ComingSoon />} />
                <Route path="/saved" element={<ComingSoon />} />
                <Route path="/profile" element={<ComingSoon />} />
                <Route path="/settings/blocked" element={<ComingSoon />} />
                <Route path="/settings/reports" element={<ComingSoon />} />
                <Route path="/settings/follow-requests" element={<ComingSoon />} />
                <Route path="/post/:id" element={<ComingSoon />} />
                {/* Legacy /u/:username aliases — kept so old links & bookmarks resolve. */}
                <Route path="/u/:username" element={<ComingSoon />} />
                <Route path="/u/:username/followers" element={<ComingSoon />} />
                <Route path="/u/:username/following" element={<ComingSoon />} />
                {/* Public profile at root — MUST stay last so it doesn't shadow any static path above. */}
                <Route path="/:username" element={<ComingSoon />} />
                <Route path="/:username/followers" element={<ComingSoon />} />
                <Route path="/:username/following" element={<ComingSoon />} />
              </>
            ) : (
              <Route element={<RequireAuth><AppShell /></RequireAuth>}>
                <Route path="/home" element={<Home />} />
                <Route path="/discover" element={<Discover />} />
                <Route path="/opinions" element={<Opinions />} />
                <Route path="/following" element={<Following />} />
                <Route path="/reels" element={<Reels />} />
                <Route path="/create" element={<Create />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/search" element={<Search />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/edit-profile" element={<EditProfile />} />
                <Route path="/saved" element={<Saved />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings/blocked" element={<BlockedUsers />} />
                <Route path="/settings/reports" element={<MyReports />} />
                <Route path="/settings/follow-requests" element={<FollowRequests />} />
                <Route path="/post/:id" element={<PostDetail />} />
              </Route>
            )}

            {/* -------- Public profile routes -------- */}
            {/* /:username (and /u/:username alias) are viewable WITHOUT an
                account. A guest sees the profile immediately with a
                minimal top-bar; after 10s a soft "Log in / Sign up"
                sheet floats in and both actions send them to the
                pre-signup Landing at `/` (never to onboarding).
                Signed-in users get the normal AppShell chrome, and this
                path deliberately bypasses the onboarding redirect so
                someone mid-onboarding can still open a shared link. */}
            {!COMING_SOON_MODE && (
              <>
                {/* Legacy aliases — old links keep working */}
                <Route path="/u/:username" element={<PublicProfileGate><UserProfile /></PublicProfileGate>} />
                <Route path="/u/:username/followers" element={<PublicProfileGate><UserListPage mode="followers" /></PublicProfileGate>} />
                <Route path="/u/:username/following" element={<PublicProfileGate><UserListPage mode="following" /></PublicProfileGate>} />
                {/* Canonical clean URLs — MUST stay last so no static app
                    route (/home, /discover, /settings, /post/:id, …) is
                    shadowed. React Router v6 ranks static > dynamic. */}
                <Route path="/:username" element={<PublicProfileGate><UserProfile /></PublicProfileGate>} />
                <Route path="/:username/followers" element={<PublicProfileGate><UserListPage mode="followers" /></PublicProfileGate>} />
                <Route path="/:username/following" element={<PublicProfileGate><UserListPage mode="following" /></PublicProfileGate>} />
              </>
            )}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
